import React from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import styled from 'styled-components';
import moment from 'moment';
import { FaRegTrashAlt } from 'react-icons/fa';
import { getTranslationKey } from '../../modules/utils';
import { CustomSpinner } from '../CustomSpinner';
import { IComment } from '../../modules/types/comments';
import { useImageCommentsModal } from './hooks/useImageCommentsModal';

const CommentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
`;

const CommentContent = styled.div`
  padding-right: 15px;
  word-break: break-word;
`;

const CommentMeta = styled.div`
  font-size: 12px;
  opacity: 0.7;
  margin-bottom: 3px;
`;

const DeleteIcon = styled(FaRegTrashAlt)`
  cursor: pointer;
  flex-shrink: 0;
  color: #f64846;
`;

const CenteredState = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;

/**
 * Comment history for a single image, opened from the chat bubble badge in the
 * Images tab, the Selections overview and the fullscreen lightbox. The
 * photographer is admin here and may delete individual comments after confirming.
 */
export const ImageCommentsModal = () => {
  const {
    isOpen,
    thread,
    isFetching,
    pendingDelete,
    closeModal,
    requestDelete,
    cancelDelete,
    confirmDelete
  } = useImageCommentsModal();

  const renderBody = () => {
    if (isFetching) {
      return (
        <CenteredState>
          <CustomSpinner />
        </CenteredState>
      );
    }

    if (!thread.length) {
      return (
        <CenteredState>{getTranslationKey('collectionSettings.comments.empty')}</CenteredState>
      );
    }

    return thread.map((comment: IComment) => (
      <CommentRow key={comment._id} data-cy={`comment-${comment._id}`}>
        <CommentContent>
          <CommentMeta>
            {comment.authorName} &middot; {moment(comment.createdAt).format('DD.MM.YYYY HH:mm')}
          </CommentMeta>
          {comment.text}
        </CommentContent>
        <DeleteIcon
          size={16}
          data-cy={`deleteComment-${comment._id}`}
          onClick={() => requestDelete(comment)}
        />
      </CommentRow>
    ));
  };

  return (
    <>
      <Modal
        centered
        isOpen={isOpen}
        wrapClassName="modal-dark"
        size="lg"
        toggle={closeModal}
        // Above the fullscreen photoswipe overlay (z-index 1600), which is one of
        // the places the badge can be clicked from. Matches CropImageModal.
        zIndex={1700}
      >
        <ModalHeader toggle={closeModal}>
          {getTranslationKey('collectionSettings.comments.modalTitle')}
        </ModalHeader>
        <ModalBody data-cy="imageCommentsModalBody">{renderBody()}</ModalBody>
        <ModalFooter>
          <Button
            data-cy="imageCommentsCloseButton"
            size="md"
            onClick={closeModal}
            className="btn btn-primary"
          >
            {getTranslationKey('collectionSettings.comments.closeButton')}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal centered isOpen={Boolean(pendingDelete)} wrapClassName="modal-dark" zIndex={1750}>
        <ModalHeader toggle={cancelDelete}>
          {getTranslationKey('collectionSettings.comments.deleteConfirmTitle')}
        </ModalHeader>
        <ModalBody data-cy="deleteCommentConfirmBody">
          {getTranslationKey('collectionSettings.comments.deleteConfirmText')}
        </ModalBody>
        <ModalFooter>
          <Button
            data-cy="deleteCommentCancelButton"
            size="md"
            onClick={cancelDelete}
            className="btn btn-primary"
          >
            {getTranslationKey('collectionSettings.comments.cancelButton')}
          </Button>
          <Button
            data-cy="deleteCommentConfirmButton"
            size="md"
            color="danger"
            onClick={confirmDelete}
            className="btn btn-primary"
          >
            {getTranslationKey('collectionSettings.comments.deleteButton')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ImageCommentsModal;
